import { engineComponent, ArrayUtils } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { Attributes } from "../Utils/Attributes";
import { Tweens } from "../Components/Effects/Tweens";
import { ImageElement, ImageElementAttributes } from "../Components/Elements/Graphics/ImageElement";
import { StageController } from "../Components/StageController";
import { CustomUtils } from "../Utils/CustomUtils";

@engineComponent(ComponentTypes.DisplayObject)
export class ImageMaskElement extends ImageElement {
    public createByNode(node: Element): void {
        super.createByNode(node);

        Tweens.removeTweens(this.maskImage, "alpha");
        Tweens.removeTweens(this.maskImage, "position");
        Tweens.removeTweens(this.maskImage, "rotation");
        Tweens.removeTweens(this.maskImage, "scale");

        if (node.hasAttribute(ImageMaskElementAttributes.MASK_IMAGE)) {
            this.maskImage = new ImageElement(
                StageController.getTextureByResourceId(node.getAttribute(ImageMaskElementAttributes.MASK_IMAGE)),
            );

            // let image = StageController.getTextureByResourceId(node.getAttribute(ImageMaskElementAttributes.MASK_IMAGE));

            if (this.maskImage != null) {
                this.maskSprite = this.maskImage;
                if (node.hasAttribute(ImageMaskElementAttributes.MASK_POSITION_X)) {
                    this.maskSprite.position.x = parseFloat(
                        node.getAttribute(ImageMaskElementAttributes.MASK_POSITION_X),
                    );
                }
                if (node.hasAttribute(ImageMaskElementAttributes.MASK_POSITION_Y)) {
                    this.maskSprite.position.y = parseFloat(
                        node.getAttribute(ImageMaskElementAttributes.MASK_POSITION_Y),
                    );
                }
                if (node.hasAttribute(ImageMaskElementAttributes.MASK_PIVOT_X)) {
                    this.maskSprite.pivot.x = parseFloat(node.getAttribute(ImageMaskElementAttributes.MASK_PIVOT_X));
                }

                this.maskSprite.scale.x = node.hasAttribute(ImageMaskElementAttributes.MASK_SCALE_X)
                    ? parseFloat(node.getAttribute(ImageMaskElementAttributes.MASK_SCALE_X))
                    : node.hasAttribute(ImageMaskElementAttributes.MASK_SCALE)
                      ? parseFloat(node.getAttribute(ImageMaskElementAttributes.MASK_SCALE))
                      : 1;
                this.maskSprite.scale.y = node.hasAttribute(ImageMaskElementAttributes.MASK_SCALE_Y)
                    ? parseFloat(node.getAttribute(ImageMaskElementAttributes.MASK_SCALE_Y))
                    : node.hasAttribute(ImageMaskElementAttributes.MASK_SCALE)
                      ? parseFloat(node.getAttribute(ImageMaskElementAttributes.MASK_SCALE))
                      : 1;

                if (node.hasAttribute(ImageMaskElementAttributes.MASK_PIVOT_Y)) {
                    this.maskSprite.pivot.y = parseFloat(node.getAttribute(ImageMaskElementAttributes.MASK_PIVOT_Y));
                }
                if (node.hasAttribute(ImageMaskElementAttributes.MASK_ROTATION)) {
                    this.maskSprite.rotation = this.degrees2radians(
                        parseFloat(node.getAttribute(ImageMaskElementAttributes.MASK_ROTATION)),
                    );
                }

                this.addChild(this.maskSprite);
                this.mask = this.maskSprite;
            } else {
                if (DEBUG) {
                    this.log(
                        "ERROR" +
                            this.componentName +
                            " no image with name for mask: " +
                            node.getAttribute(ImageElementAttributes.BITMAP_MASK),
                    );
                }
            }
        }

        this.runMaskPositionTween(node);
        this.runMaskScaleTween(node);
    }

    public updateByNode(node: Element): ImageElement {
        if (!node) {
            this.logError("No node specified for element update!");
            return;
        }
        super.updateByNode(node);

        Tweens.removeTweens(this.maskImage, "alpha");
        Tweens.removeTweens(this.maskImage, "position");
        Tweens.removeTweens(this.maskImage, "rotation");
        Tweens.removeTweens(this.maskImage, "scale");

        this.runMaskPositionTween(node);
        this.runMaskScaleTween(node);
    }

    private runMaskPositionTween(node: Element) {
        if (
            !node.hasAttribute(ImageMaskElementAttributes.MASK_POSITION_TWEEN_X) ||
            !node.hasAttribute(ImageMaskElementAttributes.MASK_POSITION_TWEEN_Y)
        ) {
            return false;
        }

        let positionTweenX: Array<number> = ArrayUtils.numberArray(
            node.getAttribute(ImageMaskElementAttributes.MASK_POSITION_TWEEN_X),
        );
        let positionTweenY: Array<number> = ArrayUtils.numberArray(
            node.getAttribute(ImageMaskElementAttributes.MASK_POSITION_TWEEN_Y),
        );

        const tweenX: Array<number> = positionTweenX || [];
        const tweenY: Array<number> = positionTweenY || [];
        const tweenEase: string = null;
        const tweenDuration: number = Attributes.assignIntAttribute(
            node,
            ImageMaskElementAttributes.MASK_POSITION_TWEEN_DURATION,
            120,
        );
        const tweenDelay: number = Attributes.assignIntAttribute(
            node,
            ImageMaskElementAttributes.MASK_POSITION_TWEEN_DELAY,
            0,
        );
        const tweenLoop: boolean = Attributes.assignBooleanAttribute(
            node,
            ImageMaskElementAttributes.MASK_POSITION_TWEEN_LOOP,
            false,
        );

        Tweens.positionTween(this.maskImage, { tweenX, tweenY, tweenEase, tweenDuration, tweenDelay, tweenLoop });
    }

    private _getPointsFromPointsAttribute(pointsString: string): any {
        return pointsString
            .replace(/\s/g, "")
            .split(";")
            .map((valuesPair: string) => {
                return /\(|\)/.test(valuesPair) ? valuesPair.slice(1, -1).split(",") : [valuesPair, valuesPair];
            })
            .map((valuesPair: Array<string>) => {
                return { x: parseFloat(valuesPair[0]), y: parseFloat(valuesPair[1]) };
            });
    }

    public runMaskScaleTween(node: Element): boolean {
        let scaleTweenX: Array<number>;
        let scaleTweenY: Array<number>;
        let scaleTween: Array<PIXI.Point>;
        let tweenValues: Array<PIXI.Point> = [];

        if (node.hasAttribute(ImageMaskElementAttributes.MASK_SCALE_TWEEN_X)) {
            scaleTweenX = ArrayUtils.numberArray(node.getAttribute(ImageMaskElementAttributes.MASK_SCALE_TWEEN_X));
        }

        if (node.hasAttribute(ImageMaskElementAttributes.MASK_SCALE_TWEEN_Y)) {
            scaleTweenY = ArrayUtils.numberArray(node.getAttribute(ImageMaskElementAttributes.MASK_SCALE_TWEEN_Y));
        }

        if (node.hasAttribute(ImageMaskElementAttributes.MASK_SCALE_TWEEN)) {
            scaleTween = this._getPointsFromPointsAttribute(
                node.getAttribute(ImageMaskElementAttributes.MASK_SCALE_TWEEN),
            ) as Array<PIXI.Point>;
        }

        if (scaleTween && scaleTween.length) {
            tweenValues = scaleTween;
        } else if (scaleTweenX && scaleTweenY) {
            if (scaleTweenX.length === scaleTweenY.length) {
                tweenValues = scaleTweenX.map((scaleXValue: number, i: number) => {
                    return {
                        x: scaleXValue,
                        y: scaleTweenY[i],
                    } as PIXI.Point;
                });
            }
        } else if (scaleTweenX) {
            tweenValues = scaleTweenX.map((scaleXValue: number, i: number) => {
                return {
                    x: scaleXValue,
                    y: this.maskImage.scale.y,
                } as PIXI.Point;
            });
        } else if (scaleTweenY) {
            tweenValues = scaleTweenY.map((scaleYValue: number, i: number) => {
                return {
                    x: this.maskImage.scale.x,
                    y: scaleYValue,
                } as PIXI.Point;
            });
        } else {
            return false;
        }

        const tweenEase: string = Attributes.assignStringAttribute(
            node,
            ImageMaskElementAttributes.MASK_SCALE_TWEEN_EASE,
            null,
        );
        const tweenDuration: number = Attributes.assignIntAttribute(
            node,
            ImageMaskElementAttributes.MASK_SCALE_TWEEN_DURATION,
            120,
        );
        const tweenDelay: number = Attributes.assignIntAttribute(
            node,
            ImageMaskElementAttributes.MASK_SCALE_TWEEN_DELAY,
            0,
        );
        const tweenLoop: boolean = Attributes.assignBooleanAttribute(
            node,
            ImageMaskElementAttributes.MASK_SCALE_TWEEN_LOOP,
            null,
        );

        Tweens.scaleTween(this.maskImage, { tweenValues, tweenEase, tweenDuration, tweenDelay, tweenLoop });
    }

    /**
     * Clone method used for cloning instance of this element with his main properties.
     * @method clone
     * @return ImageElement
     */
    public clone(): ImageMaskElement {
        let element: ImageMaskElement = new ImageMaskElement();
        element.removeChildren();

        element.position.x = this.position.x;
        element.position.y = this.position.y;
        element.pivot.x = this.pivot.x;
        element.pivot.y = this.pivot.y;
        element.scale.x = this.scale.x;
        element.scale.y = this.scale.y;
        element.rotation = this.rotation;
        element.visible = this.visible;
        element.alpha = this.alpha;
        element.anchor = this.anchor;
        element.tint = this.tint;
        element.blendMode = CustomUtils.getBlendMode(this.blendMode);
        element.name = this.name;

        if (this.texture != null) {
            element.texture = this.texture;
        }

        if (this.maskImage != null) {
            element.maskImage = this.maskImage.clone();
            element.maskSprite = element.maskImage;

            element.addChild(element.maskSprite);
            element.mask = element.maskSprite;
        }

        element.markAsCloned(); // must be called last before return!

        return element;
    }
}

const ImageMaskElementAttributes = {
    MASK_IMAGE: "MaskImage",
    MASK_POSITION_X: "MaskPositionX",
    MASK_POSITION_Y: "MaskPositionY",
    MASK_PIVOT_X: "MaskPivotX",
    MASK_PIVOT_Y: "MaskPivotY",
    MASK_SCALE: "MaskScale",
    MASK_SCALE_X: "MaskScaleX",
    MASK_SCALE_Y: "MaskScaleY",
    MASK_ROTATION: "MaskRotation",

    MASK_POSITION_TWEEN_X: "MaskPositionTweenX",
    MASK_POSITION_TWEEN_Y: "MaskPositionTweenY",
    MASK_POSITION_TWEEN_DURATION: "MaskPositionTweenDuration",
    MASK_POSITION_TWEEN_DELAY: "MaskPositionTweenDelay",
    MASK_POSITION_TWEEN_LOOP: "MaskPositionTweenLoop",

    MASK_SCALE_TWEEN_X: "MaskScaleTweenX",
    MASK_SCALE_TWEEN_Y: "MaskScaleTweenY",
    MASK_SCALE_TWEEN: "MaskScaleTween",
    MASK_SCALE_TWEEN_EASE: "MaskScaleTweenEase",
    MASK_SCALE_TWEEN_LOOP: "MaskScaleTweenLoop",
    MASK_SCALE_TWEEN_DURATION: "MaskScaleTweenDuration",
    MASK_SCALE_TWEEN_DELAY: "MaskScaleTweenDelay",
};



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/ImageMaskElement.ts
 **/